import type { JSX } from 'react';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { useStore, Updater } from 'dg-web-shared/lib/Flux.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { thenElse } from 'dg-web-shared/lib/MaybeV2.ts';
import { Translation } from '../../common/i18n/Text.ts';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import * as OperatorLoginsState from '../../common/state/OperatorLoginsState.ts';
import {
    getLoginById,
    getLoginTitle,
} from '../../common/state/OperatorLoginsState.ts';
import {
    IconButton40px,
    IconButton40pxType,
} from '../../ui/buttons/IconButton.tsx';
import { ChevronRightColumn } from '../../ui/table/ColumImplementations.tsx';
import {
    ColumnWidth,
    DefaultTableRow,
    EmptyTableHeaderColumn,
    SortDirection,
    Table,
    TableBody,
    TableColumn,
    TableHeader,
    TableHeaderColumn,
} from '../../ui/table/Table.tsx';
import * as CustomerClearanceTexts from '../i18n/CustomerClearanceTexts.ts';
import * as CustomerClearanceState from '../state/CustomerClearanceState.ts';
import { ModalSpecialTabResultRefresher } from '../../ui/layout/TabContent.tsx';
import { css } from '@emotion/css';

export interface CustomerClearanceTableTexts {
    createdDate: Translation;
    createdBy: Translation;
    customerNr: Translation;
    remark: Translation;
    permitTypeName: Translation;
}

interface ColumnHeaderProps {
    name: string;
    width: ColumnWidth;
    active?: boolean;
}

const ColumnHeader = (p: ColumnHeaderProps) => {
    return (
        <TableHeaderColumn
            name={p.name}
            direction={p.active ? SortDirection.Ascending : null}
            onClick={null}
            selected={p.active ? true : false}
            width={p.width}
        />
    );
};

interface CustomerClearanceTableHeaderProps {
    texts: CustomerClearanceTableTexts;
}

const CustomerClearanceTableHeader = (
    p: CustomerClearanceTableHeaderProps,
): JSX.Element => {
    return (
        <TableHeader>
            <EmptyTableHeaderColumn width={ColumnWidth._24px} />
            <ColumnHeader
                name={p.texts.createdDate()}
                width={ColumnWidth._136px}
            />
            <ColumnHeader
                name={p.texts.createdBy()}
                width={ColumnWidth._136px}
            />
            <ColumnHeader
                name={p.texts.customerNr()}
                width={ColumnWidth._208px}
            />
            <ColumnHeader name={p.texts.remark()} width={ColumnWidth._208px} />
            <ColumnHeader
                name={p.texts.permitTypeName()}
                width={ColumnWidth.variable}
            />
            <EmptyTableHeaderColumn width={ColumnWidth._24px} />
        </TableHeader>
    );
};

interface CustomerClearanceItemProps {
    clearance: CustomerClearanceState.CustomerClearance;
    operatorLogins: OperatorLoginsState.OperatorLogin[];
    texts: CustomerClearanceTableTexts;
    update: Updater;
}

const CustomerClearanceItem = (p: CustomerClearanceItemProps): JSX.Element => {
    return (
        <DefaultTableRow
            onClick={() =>
                p.update(store =>
                    CustomerClearanceState.Detail.stateWrite(store, {
                        selectedClearance: p.clearance,
                    }),
                )
            }
        >
            <TableColumn width={ColumnWidth._24px} />
            <TableColumn width={ColumnWidth._136px}>
                {Formatter.dayMonthYear(p.clearance.created)}
            </TableColumn>
            <TableColumn width={ColumnWidth._136px}>
                {thenElse(
                    getLoginById(p.operatorLogins, p.clearance.adminId),
                    l => getLoginTitle(l),
                    '',
                )}
            </TableColumn>
            <TableColumn width={ColumnWidth._208px}>
                {p.clearance.customerNr}
            </TableColumn>
            <TableColumn width={ColumnWidth._208px}>
                {CustomerClearanceState.remark(p.clearance)}
            </TableColumn>
            <TableColumn width={ColumnWidth.variable}>
                {p.clearance.permitTypeName}
            </TableColumn>
            <ChevronRightColumn />
        </DefaultTableRow>
    );
};

export function CustomerClearanceTable() {
    const { storeState, update } = useStore(store => ({
        customerClearances: CustomerClearanceState.List.get(store),
        operatorData: OperatorDataState.get(store),
        operatorLogins: OperatorLoginsState.List.get(store),
    }));
    const { language } = useLanguage();

    const texts = CustomerClearanceTexts.customerClearanceTableTexts[language];
    return (
        <>
            <ModalSpecialTabResultRefresher
                onRefreshClick={() =>
                    update(CustomerClearanceState.List.forceRefetch, true)
                }
                successRequest={
                    storeState.customerClearances.statusCode.statusCode === 200
                }
                pendingRequest={storeState.customerClearances.pending}
            />
            <div>
                <div
                    className={css({
                        position: 'absolute',
                        height: '32px',
                        top: '15px',
                        right: '21px',
                    })}
                >
                    {storeState.operatorData.data &&
                        storeState.operatorData.data
                            .hasAWhitelistCustomerPermittype && (
                            <IconButton40px
                                type={IconButton40pxType.add}
                                onClick={() =>
                                    update(store =>
                                        CustomerClearanceState.Add.stateWrite(
                                            store,
                                            {
                                                createCustomerClearanceOpen: true,
                                            },
                                        ),
                                    )
                                }
                            />
                        )}
                </div>
                <div
                    className={css({
                        position: 'absolute',
                        top: '66px',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                    })}
                >
                    <Table>
                        <CustomerClearanceTableHeader texts={texts} />
                        <TableBody>
                            {storeState.customerClearances.data.map(c => (
                                <CustomerClearanceItem
                                    key={c.contractId}
                                    clearance={c}
                                    operatorLogins={
                                        storeState.operatorLogins.data
                                    }
                                    texts={texts}
                                    update={update}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </>
    );
}
